import React, { useState, useEffect } from 'react';
import { generateToken, TOKEN_HEADER } from '../../utils/tokenUtils';
import { API_ENDPOINTS } from '../../config/constants';
import '../../styles/admin.css';

const EventNotifications = () => {
  const [activeTab, setActiveTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [emailPreview, setEmailPreview] = useState(null);
  
  // Template editor state
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    subject_template: '',
    body_template: '',
    is_active: true
  });

  // Override editor state
  const [overrideForm, setOverrideForm] = useState({
    event_id: '',
    reminder_type: '24h',
    subject_template: '',
    body_template: '',
    is_active: true
  });

  // Delivery filters
  const [deliveryFilters, setDeliveryFilters] = useState({
    event_id: '',
    status: '',
    limit: 50,
    offset: 0
  });
  const [deliveryTotal, setDeliveryTotal] = useState(0);

  const getApiError = (data, fallback) => {
    if (Array.isArray(data?.errors) && data.errors.length > 0) {
      return data.errors.map((item) => item.msg).join(' ');
    }
    return data?.message || fallback;
  };

  useEffect(() => {
    if (activeTab === 'templates') {
      fetchTemplates();
    } else if (activeTab === 'overrides') {
      fetchOverrides();
    } else if (activeTab === 'deliveries') {
      fetchDeliveries();
    }
    // Tab changes are the trigger; filter changes are applied explicitly by the user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (!emailPreview) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setEmailPreview(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [emailPreview]);

  const openEmailPreview = (label, subject, html) => {
    const sampleValues = {
      event_title: 'Leading Through AI: Live Workshop',
      event_start_datetime: 'Sunday, August 23, 2026 at 10:00 AM UTC',
      event_location: 'Microsoft Teams',
      recipient_name: 'Alex Morgan',
      recipient_email: 'alex@example.com',
      tracking_pixel_url: ''
    };
    const renderSamples = (value) => Object.entries(sampleValues).reduce(
      (rendered, [key, sample]) => rendered.replace(new RegExp(`{{\\s*${key}\\s*}}`, 'g'), sample),
      value || ''
    );

    setEmailPreview({
      label,
      subject: renderSamples(subject),
      html: renderSamples(html)
    });
  };

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/admin/event-notifications/templates`, {
        headers: {
          'Content-Type': 'application/json',
          [TOKEN_HEADER]: generateToken()
        }
      });
      const data = await response.json();
      if (data.success) {
        setTemplates(data.templates);
      } else {
        setError('Failed to fetch templates');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchOverrides = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/admin/event-notifications/overrides`, {
        headers: {
          'Content-Type': 'application/json',
          [TOKEN_HEADER]: generateToken()
        }
      });
      const data = await response.json();
      if (data.success) {
        setOverrides(data.overrides);
      } else {
        setError('Failed to fetch overrides');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeliveries = async (filters = deliveryFilters) => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams(filters);
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/admin/event-notifications/deliveries?${params}`, {
        headers: {
          'Content-Type': 'application/json',
          [TOKEN_HEADER]: generateToken()
        }
      });
      const data = await response.json();
      if (data.success) {
        setDeliveries(data.deliveries);
        setDeliveryTotal(data.total);
      } else {
        setError('Failed to fetch deliveries');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      subject_template: template.subject_template,
      body_template: template.body_template,
      is_active: template.is_active
    });
  };

  const handleSaveTemplate = async () => {
    try {
      setError(null);
      setSuccess(null);
      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/admin/event-notifications/templates/${editingTemplate.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            [TOKEN_HEADER]: generateToken()
          },
          body: JSON.stringify(templateForm)
        }
      );
      const data = await response.json();
      if (data.success) {
        setSuccess('Template updated successfully');
        setEditingTemplate(null);
        fetchTemplates();
      } else {
        setError(getApiError(data, 'Failed to update template'));
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSaveOverride = async () => {
    try {
      setError(null);
      setSuccess(null);
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/admin/event-notifications/overrides`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [TOKEN_HEADER]: generateToken()
        },
        body: JSON.stringify(overrideForm)
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('Override saved successfully');
        setOverrideForm({
          event_id: '',
          reminder_type: '24h',
          subject_template: '',
          body_template: '',
          is_active: true
        });
        fetchOverrides();
      } else {
        setError(getApiError(data, 'Failed to save override'));
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteOverride = async (id) => {
    if (!window.confirm('Are you sure you want to delete this override?')) return;
    
    try {
      setError(null);
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/admin/event-notifications/overrides/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          [TOKEN_HEADER]: generateToken()
        }
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('Override deleted successfully');
        fetchOverrides();
      } else {
        setError('Failed to delete override');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  const renderTemplates = () => (
    <div className="notification-templates">
      <h3>Global Notification Templates</h3>
      <p className="help-text">
        Edit the default templates for event reminders. Use placeholders:{' '}
        <code>{'{{event_title}}'}</code>, <code>{'{{event_start_datetime}}'}</code>,{' '}
        <code>{'{{event_location}}'}</code>, <code>{'{{recipient_name}}'}</code>,{' '}
        <code>{'{{tracking_pixel_url}}'}</code>
      </p>
      
      {templates.map(template => (
        <div key={template.id} className="template-card">
          <div className="template-header">
            <h4>{template.reminder_type} Reminder</h4>
            <span className={`status-badge ${template.is_active ? 'active' : 'inactive'}`}>
              {template.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
          
          {editingTemplate?.id === template.id ? (
            <div className="template-editor">
              <div className="form-group">
                <label>Subject Template</label>
                <input
                  type="text"
                  value={templateForm.subject_template}
                  onChange={(e) => setTemplateForm({...templateForm, subject_template: e.target.value})}
                  className="form-control"
                />
              </div>
              
              <div className="form-group">
                <label>Body Template (HTML)</label>
                <textarea
                  value={templateForm.body_template}
                  onChange={(e) => setTemplateForm({...templateForm, body_template: e.target.value})}
                  className="form-control"
                  rows="10"
                />
              </div>
              
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={templateForm.is_active}
                    onChange={(e) => setTemplateForm({...templateForm, is_active: e.target.checked})}
                  />
                  {' '}Active
                </label>
              </div>
              
              <div className="button-group">
                <button onClick={handleSaveTemplate} className="btn btn-primary">Save</button>
                <button
                  type="button"
                  onClick={() => openEmailPreview(
                    `${template.reminder_type} reminder`,
                    templateForm.subject_template,
                    templateForm.body_template
                  )}
                  className="btn btn-secondary"
                >
                  Preview
                </button>
                <button onClick={() => setEditingTemplate(null)} className="btn btn-secondary">Cancel</button>
              </div>
            </div>
          ) : (
            <div className="template-preview">
              <p><strong>Subject:</strong> {template.subject_template}</p>
              <p><strong>Last Updated:</strong> {formatDate(template.updated_at)}</p>
              <button onClick={() => handleEditTemplate(template)} className="btn btn-secondary">
                Edit
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const renderOverrides = () => (
    <div className="notification-overrides">
      <h3>Event-Specific Overrides</h3>
      <p className="help-text">
        Create custom notification templates for specific events.
      </p>
      
      <div className="override-form">
        <h4>Add/Update Override</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Event ID</label>
            <input
              type="number"
              value={overrideForm.event_id}
              onChange={(e) => setOverrideForm({...overrideForm, event_id: e.target.value})}
              className="form-control"
            />
          </div>
          
          <div className="form-group">
            <label>Reminder Type</label>
            <select
              value={overrideForm.reminder_type}
              onChange={(e) => setOverrideForm({...overrideForm, reminder_type: e.target.value})}
              className="form-control"
            >
              <option value="24h">24 Hours</option>
              <option value="1h">1 Hour</option>
              <option value="2m">2 Minutes</option>
            </select>
          </div>
        </div>
        
        <div className="form-group">
          <label>Subject Template</label>
          <input
            type="text"
            value={overrideForm.subject_template}
            onChange={(e) => setOverrideForm({...overrideForm, subject_template: e.target.value})}
            className="form-control"
          />
        </div>
        
        <div className="form-group">
          <label>Body Template (HTML)</label>
          <textarea
            value={overrideForm.body_template}
            onChange={(e) => setOverrideForm({...overrideForm, body_template: e.target.value})}
            className="form-control"
            rows="8"
          />
        </div>
        
        <div className="button-group">
          <button onClick={handleSaveOverride} className="btn btn-primary">Save Override</button>
          <button
            type="button"
            onClick={() => openEmailPreview(
              `${overrideForm.reminder_type} event override`,
              overrideForm.subject_template,
              overrideForm.body_template
            )}
            className="btn btn-secondary"
          >
            Preview
          </button>
        </div>
      </div>
      
      <div className="overrides-list">
        <h4>Existing Overrides</h4>
        {overrides.length === 0 ? (
          <p>No overrides configured.</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Event ID</th>
                <th>Event Title</th>
                <th>Reminder Type</th>
                <th>Subject</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {overrides.map(override => (
                <tr key={override.id}>
                  <td>{override.event_id}</td>
                  <td>{override.event_title}</td>
                  <td>{override.reminder_type}</td>
                  <td className="subject-preview">{override.subject_template}</td>
                  <td>{override.is_active ? '✓' : '✗'}</td>
                  <td>
                    <button 
                      onClick={() => handleDeleteOverride(override.id)} 
                      className="btn btn-danger btn-sm"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );

  const renderDeliveries = () => (
    <div className="notification-deliveries">
      <h3>Delivery Audit</h3>
      <p className="help-text">
        View notification delivery status and approximate open tracking. 
        <strong>Note:</strong> Email opens are approximate (proxy/prefetch affects accuracy).
      </p>
      
      <div className="delivery-filters">
        <div className="form-row">
          <div className="form-group">
            <label>Event ID</label>
            <input
              type="number"
              value={deliveryFilters.event_id}
              onChange={(e) => setDeliveryFilters({...deliveryFilters, event_id: e.target.value, offset: 0})}
              className="form-control"
              placeholder="Filter by event"
            />
          </div>
          
          <div className="form-group">
            <label>Status</label>
            <select
              value={deliveryFilters.status}
              onChange={(e) => setDeliveryFilters({...deliveryFilters, status: e.target.value, offset: 0})}
              className="form-control"
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
              <option value="manual_review">Manual Review</option>
            </select>
          </div>
          
          <div className="form-group">
            <button onClick={() => fetchDeliveries()} className="btn btn-primary">
              Apply Filters
            </button>
          </div>
        </div>
      </div>
      
      <div className="delivery-stats">
        <p>Total: {deliveryTotal} | Showing: {deliveries.length}</p>
      </div>
      
      <div className="table-responsive">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Event</th>
              <th>Reminder</th>
              <th>Recipient</th>
              <th>Scheduled</th>
              <th>Status</th>
              <th>Sent At</th>
              <th>Opens (Approx)</th>
              <th>Attempts</th>
            </tr>
          </thead>
          <tbody>
            {deliveries.map(delivery => (
              <tr key={delivery.id}>
                <td title={delivery.event_title}>
                  {delivery.event_id}: {delivery.event_title?.substring(0, 30)}...
                </td>
                <td>{delivery.reminder_type}</td>
                <td>{delivery.recipient_email}</td>
                <td>{formatDate(delivery.scheduled_for)}</td>
                <td>
                  <span className={`status-badge status-${delivery.status}`}>
                    {delivery.status}
                  </span>
                </td>
                <td>{formatDate(delivery.sent_at)}</td>
                <td>
                  {delivery.open_count > 0 ? (
                    <span title={`First: ${formatDate(delivery.first_opened_at)}, Last: ${formatDate(delivery.last_opened_at)}`}>
                      {delivery.open_count} ✓
                    </span>
                  ) : (
                    <span>0</span>
                  )}
                </td>
                <td>{delivery.attempts}/{delivery.max_attempts}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <div className="pagination">
        <button
          onClick={() => {
            const offset = Math.max(0, deliveryFilters.offset - deliveryFilters.limit);
            setDeliveryFilters({...deliveryFilters, offset});
            fetchDeliveries({...deliveryFilters, offset});
          }}
          disabled={deliveryFilters.offset === 0}
          className="btn btn-secondary"
        >
          Previous
        </button>
        <span>Page {Math.floor(deliveryFilters.offset / deliveryFilters.limit) + 1}</span>
        <button
          onClick={() => {
            const offset = deliveryFilters.offset + deliveryFilters.limit;
            setDeliveryFilters({...deliveryFilters, offset});
            fetchDeliveries({...deliveryFilters, offset});
          }}
          disabled={deliveryFilters.offset + deliveryFilters.limit >= deliveryTotal}
          className="btn btn-secondary"
        >
          Next
        </button>
      </div>
    </div>
  );

  return (
    <div className="event-notifications-admin">
      <h2>Event Notifications Management</h2>
      
      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}
      
      <div className="tabs">
        <button
          className={activeTab === 'templates' ? 'tab-active' : ''}
          onClick={() => setActiveTab('templates')}
        >
          Global Templates
        </button>
        <button
          className={activeTab === 'overrides' ? 'tab-active' : ''}
          onClick={() => setActiveTab('overrides')}
        >
          Event Overrides
        </button>
        <button
          className={activeTab === 'deliveries' ? 'tab-active' : ''}
          onClick={() => setActiveTab('deliveries')}
        >
          Delivery Audit
        </button>
      </div>
      
      <div className="tab-content">
        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <>
            {activeTab === 'templates' && renderTemplates()}
            {activeTab === 'overrides' && renderOverrides()}
            {activeTab === 'deliveries' && renderDeliveries()}
          </>
        )}
      </div>

      {emailPreview && (
        <div className="email-preview-overlay" onClick={() => setEmailPreview(null)}>
          <aside
            className="email-preview-drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby="email-preview-title"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="email-preview-header">
              <div>
                <span className="email-preview-eyebrow">HTML email preview</span>
                <h3 id="email-preview-title">{emailPreview.label}</h3>
              </div>
              <button
                type="button"
                className="email-preview-close"
                onClick={() => setEmailPreview(null)}
                aria-label="Close preview"
                title="Close preview"
              >
                ×
              </button>
            </header>
            <div className="email-preview-subject">
              <span>Subject</span>
              <strong>{emailPreview.subject || 'No subject'}</strong>
            </div>
            <div className="email-preview-canvas">
              <iframe
                title="Rendered HTML email"
                srcDoc={emailPreview.html || '<p style="font-family:sans-serif">No HTML body entered.</p>'}
                sandbox=""
              />
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default EventNotifications;
